import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API, BACKEND_URL, authGet } from '@/lib/api';
import { ArrowLeft, Share2, MoreHorizontal, Grid3x3, List, Heart, MessageCircle, Download, Play, Plus, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';


export default function EventGalleryNew({ eventId, eventTitle, eventDate, onBack, shareCode, isOwner }) {
  const navigate = useNavigate();
  const [view, setView] = useState('timeline'); // timeline or albums
  const [viewMode, setViewMode] = useState('masonry'); // masonry or list
  const [moments, setMoments] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGalleryData();
  }, [eventId, shareCode]);

  const fetchGalleryData = async () => {
    try {
      let photosRes;
      if (shareCode) {
        // Public access
        photosRes = await axios.get(`${BACKEND_URL}/api/public/event/${shareCode}/photos`);
      } else {
        // Admin/Private access
        photosRes = await authGet(`/events/${eventId}/photos`);
      }

      setPhotos(photosRes.data || []);

      // Create moments from recent uploads
      const recentPhotos = (photosRes.data || []).slice(0, 10);
      setMoments(recentPhotos);
    } catch (error) {
      console.error('Failed to fetch gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-gray-900 pb-24">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-base font-bold">{eventTitle}</h1>
              <p className="text-xs text-gray-500">
                {eventDate && format(parseISO(eventDate), 'MMM dd, yyyy', { locale: de }).toUpperCase()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="p-2 hover:bg-gray-100 rounded-full">
              <Share2 className="w-5 h-5" />
            </button>
            <button className="p-2 hover:bg-gray-100 rounded-full">
              <MoreHorizontal className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* View Toggle */}
        <div className="px-4 pb-4">
          <div className="flex h-10 w-full items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800 p-1">
            <button
              onClick={() => setView('timeline')}
              className={`flex-1 h-full flex items-center justify-center rounded-md text-xs font-bold transition-all ${view === 'timeline'
                ? 'bg-white dark:bg-indigo-600 shadow-sm text-indigo-600 dark:text-white'
                : 'text-gray-500'
                }`}
            >
              Timeline
            </button>
            <button
              onClick={() => setView('albums')}
              className={`flex-1 h-full flex items-center justify-center rounded-md text-xs font-bold transition-all ${view === 'albums'
                ? 'bg-white dark:bg-indigo-600 shadow-sm text-indigo-600 dark:text-white'
                : 'text-gray-500'
                }`}
            >
              Albums
            </button>
          </div>
        </div>
      </div>

      {/* Moments Section */}
      <div className="pt-4 px-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-gray-900 dark:text-white text-lg font-bold">Moments</h3>
          <button className="text-indigo-600 text-xs font-bold px-3 py-1 bg-indigo-50 dark:bg-indigo-900/20 rounded-full hover:bg-indigo-100 transition-colors">
            Select
          </button>
        </div>

        {/* Horizontal Scroll - Moments */}
        <div className="flex overflow-x-auto gap-3 no-scrollbar pb-4 -mx-4 px-4 snap-x">
          {/* Add Button */}
          {(isOwner || shareCode) && (
            <div
              onClick={() => isOwner ? navigate(`/event/${eventId}/upload`) : null}
              className="snap-start shrink-0 w-24 flex flex-col gap-2 group cursor-pointer"
            >
              <div className="relative w-24 h-36 rounded-2xl overflow-hidden bg-gray-200 dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 flex flex-col items-center justify-center text-gray-400">
                <Camera className="w-8 h-8 mb-1" />
                <span className="text-[10px] font-bold">Add</span>
              </div>
              <p className="text-[10px] text-center text-gray-500 font-medium truncate">Upload</p>
            </div>
          )}

          {/* Moment Cards */}
          {moments.map((moment, i) => (
            <motion.div
              key={moment.id || i}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.05 }}
              className="snap-start shrink-0 w-24 flex flex-col gap-2 group cursor-pointer"
            >
              <div className="relative w-24 h-36 rounded-2xl overflow-hidden shadow-sm">
                <div
                  className="absolute inset-0 bg-cover bg-center transform transition-transform duration-500 group-hover:scale-110"
                  style={{ backgroundImage: `url(${moment.thumbnail?.startsWith('http') || moment.url?.startsWith('http') ? (moment.thumbnail || moment.url) : (moment.thumbnail?.startsWith('/') || moment.url?.startsWith('/') ? `${BACKEND_URL}${moment.thumbnail || moment.url}` : `${BACKEND_URL}/api/media/${moment.thumbnail || moment.url}`)})` }}
                />
                {moment.is_video && (
                  <div className="absolute bottom-2 inset-x-0 flex justify-center">
                    <span className="text-white text-[10px] font-bold flex items-center gap-1 bg-black/30 px-2 py-0.5 rounded-full backdrop-blur-sm">
                      <Play className="w-3 h-3" /> {moment.duration || '0:42'}
                    </span>
                  </div>
                )}
              </div>
              <p className="text-[10px] text-center text-gray-600 dark:text-gray-400 font-medium truncate">
                {moment.caption || moment.uploaded_by || 'Photo'}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Latest Photos */}
      <div className="pt-6 px-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-gray-900 dark:text-white text-lg font-bold">Latest Photos</h3>
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('masonry')}
              className={`p-2 rounded-lg transition-colors ${viewMode === 'masonry' ? 'text-indigo-600 bg-indigo-50' : 'text-gray-400 hover:text-indigo-600'
                }`}
            >
              <Grid3x3 className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'text-indigo-600 bg-indigo-50' : 'text-gray-400 hover:text-indigo-600'
                }`}
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Masonry Grid */}
        <div className="grid grid-cols-2 gap-3 pb-20">
          {photos.map((photo, index) => (
            <motion.div
              key={photo.id || index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.03 }}
              className={`relative group rounded-2xl overflow-hidden cursor-pointer shadow-sm hover:shadow-md transition-all ${index % 3 === 0 ? 'row-span-2' : ''
                }`}
            >
              <img
                src={photo.thumbnail?.startsWith('http') || photo.url?.startsWith('http') ? (photo.thumbnail || photo.url) : (photo.thumbnail?.startsWith('/') || photo.url?.startsWith('/') ? `${BACKEND_URL}${photo.thumbnail || photo.url}` : `${BACKEND_URL}/api/media/${photo.thumbnail || photo.url}`)}
                alt=""
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 inset-x-0 p-2 bg-gradient-to-t from-black/70 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="flex items-center justify-between text-white">
                  <div className="flex items-center gap-3">
                    <button className="flex items-center gap-1">
                      <Heart className="w-4 h-4" />
                      <span className="text-[10px] font-medium">{photo.likes || 0}</span>
                    </button>
                    <button className="flex items-center gap-1">
                      <MessageCircle className="w-4 h-4" />
                      <span className="text-[10px] font-medium">{photo.comments || 0}</span>
                    </button>
                  </div>
                  <button>
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {photos.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📷</div>
            <p className="text-gray-500">Noch keine Fotos</p>
          </div>
        )}
      </div>

      {/* FAB */}
      {(isOwner || shareCode) && (
        <button
          onClick={() => isOwner ? navigate(`/event/${eventId}/upload`) : null}
          className="fixed bottom-8 right-6 bg-gray-900 dark:bg-indigo-600 text-white rounded-full p-4 shadow-2xl hover:scale-105 active:scale-95 transition-all z-50 flex items-center gap-2"
        >
          <Plus className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}
